/**
 * Root app — auth shell + tenant theme + router.
 *
 * Routes (per docs/00_PLATFORM_PLAN.md § Product surface):
 *
 *   /                    → marketing landing (redirect to sahas.com in prod)
 *   /login, /signup      → auth flows
 *   /onboarding          → org creation + first gateway claim wizard
 *   /app/devices         → Tier 1
 *   /app/dashboards      → Tier 1
 *   /app/admin           → Tier 1 + platform admin
 *   /app/twin/*          → Tier 2 (lazy-loaded — only loads bundle when
 *                          tenant has pack:foundry enabled)
 *
 * Auth + TenantTheme + Pack gating wrap every authenticated route.
 */

import { lazy, Suspense, useEffect, useRef, type ReactNode } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useAuth as useOidcAuth } from "react-oidc-context";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { api } from "@/lib/api-client";

import { OrgThemeProvider } from "@/lib/theme/org-theme-provider";
import { resolveBrandFromHostname } from "@/lib/brand-resolver";
import { AuthProvider, RequireAuth, useAuth } from "@/lib/auth";
import { OrgProvider, useOrg } from "@/lib/org-context";
import { AppLanding } from "@/routes/app-landing";
import { HubIndex } from "@/routes/hub-index";
import { CAPTURE, CATALOG, DEVICES } from "@/lib/nav/hubs";
import { HubLayout } from "@/components/hub/HubTabs";
import { peekInviteToken, stashInviteToken } from "@/lib/pending-invite";
import { PlantProvider } from "@/lib/plant/plant-store";
import { TracePathProvider } from "@foundry/trace-path-context";
import { ComputedTagsProvider } from "@/lib/compute/store";
import { DashboardsProvider } from "@/lib/dashboard/store";
import { VersionWatcher } from "@/lib/version-watcher";
import { AlarmsProvider } from "@/lib/alarms/store";
import { GatewaysProvider } from "@/lib/iot/gateways-store";
import { ToastProvider } from "@/lib/ui/toast";
import { CopilotProvider } from "@/lib/copilot/store";
import { ReportsProvider } from "@/lib/reports/store";
import { ImportsProvider } from "@/lib/imports/store";
import { InsightsProvider } from "@/lib/insights/store";

import { Landing } from "@/routes/landing";
import { Login } from "@/routes/login";
import { Signup } from "@/routes/signup";
import { PrivacyPolicy } from "@/routes/privacy";
import { TermsOfService } from "@/routes/terms";
import { Onboarding } from "@/routes/onboarding";
import { Register } from "@/routes/register";
import { PendingApproval } from "@/routes/pending-approval";
import { AccessDenied } from "@/routes/access-denied";
import { AcceptInvite } from "@/routes/accept-invite";
import { AppLayout } from "@/routes/_layout";
import { Devices } from "@/routes/iot/devices";
import { Connect } from "@/routes/connect";
import { DataEntry } from "@/routes/iot/data-entry";
import { Dashboards } from "@/routes/iot/dashboards";
import { DashboardView } from "@/routes/iot/dashboard-view";
import { Alarms } from "@/routes/iot/alarms";
import { ComputedTags } from "@/routes/iot/computed-tags";
import { SettingsPage } from "@/routes/platform/admin";
import { PlatformConsole } from "@/routes/platform/platform-console";
import { SimStudio } from "@/routes/sim/sim-studio";
import { WidgetsPreview } from "@/routes/_widgets";
import { VendorDashboard } from "@/routes/vendor/dashboard";
import { VendorOnboard } from "@/routes/vendor/onboard";
import { VendorCustomerDetail } from "@/routes/vendor/customer-detail";

// Tier 2 — lazy-loaded so non-foundry tenants never download this bundle.
const TwinRoutes = lazy(() =>
  import("@/routes/twin").then((m) => ({ default: m.TwinRoutes })),
);
// A station's view (station workspaces, step 5): a foundry page, lazy as the twin's are.
const StationViewPage = lazy(() =>
  import("@/routes/stations/station-view").then((m) => ({ default: m.StationViewPage })),
);

// Public Insights (marketing white-papers) — lazy so the long-form content +
// Recharts illustrative charts stay out of the main landing bundle. These are
// PUBLIC routes (unauthenticated marketing site), siblings of /, /login.
const InsightsIndex = lazy(() =>
  import("@/routes/insights/index").then((m) => ({ default: m.InsightsIndex })),
);
const InsightsArticle = lazy(() =>
  import("@/routes/insights/article").then((m) => ({
    default: m.InsightsArticle,
  })),
);

// The tutorial player (#122): lazy, so the guided-tour engine and its mock screens stay out of the main bundle.
const TutorialPlayerRoute = lazy(() =>
  import("@/routes/tutorials/player").then((m) => ({
    default: m.TutorialPlayerRoute,
  })),
);

// React Query devtools — lazy + dev-only so they never ship to prod.
const ReactQueryDevtools = lazy(() =>
  import("@tanstack/react-query-devtools").then((m) => ({
    default: m.ReactQueryDevtools,
  })),
);

/**
 * One QueryClient for the app. Reads never refetch on window focus (the
 * per-hook staleTime + refetchInterval drive freshness); retries skip 4xx
 * (a 401/404 won't fix itself) and cap at 2 for network/5xx.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (count, err) => {
        const status = (err as { status?: number } | null)?.status;
        if (typeof status === "number" && status >= 400 && status < 500) {
          return false;
        }
        return count < 2;
      },
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <AuthProvider>
        <OrgProvider>
        <OrgThemeProvider initial={resolveBrandFromHostname()}>
          <ToastProvider>
          <VersionWatcher />
          <PlantProvider>
          <TracePathProvider>
          <ComputedTagsProvider>
          <DashboardsProvider>
          <ReportsProvider>
          <ImportsProvider>
          <InsightsProvider>
          <AlarmsProvider>
          <GatewaysProvider>
          <CopilotProvider>
          <Routes>
            {/* Public */}
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />

            {/*
              Legal pages — PUBLIC (unauthenticated). Reachable from the
              landing/marketing footers and the sign-up consent line, so they
              must sit alongside /login + /signup, OUTSIDE RequireAuth /
              RequireApproved / RequireOrg. Self-contained routes (no marketing
              chrome / pack providers).
            */}
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />

            {/*
              Public Insights — marketing white-papers reachable from the
              landing page. Deliberately OUTSIDE /app + RequireAuth so
              unauthenticated visitors can read them. Lazy-loaded.
            */}
            <Route
              path="/insights"
              element={
                <Suspense fallback={<LazyFallback label="insights" />}>
                  <InsightsIndex />
                </Suspense>
              }
            />
            <Route
              path="/insights/:slug"
              element={
                <Suspense fallback={<LazyFallback label="insights" />}>
                  <InsightsArticle />
                </Suspense>
              }
            />

            {/*
              OIDC callback. The AuthProvider auto-detects code+state
              in the URL, processes the exchange, then onSigninCallback
              (in lib/auth.tsx) navigates to /app. This route exists
              just to render a placeholder during the sub-second
              exchange — without it the user would briefly see the
              404 page.
            */}
            <Route path="/auth/callback" element={<AuthCallback />} />

            {/*
              External-IdP (Google/Apple) return. Zitadel 302s the browser
              here after the IdP round-trip with ?authRequest=&id=&token=.
              IdpCallback exchanges the completed idp_intent for the OIDC
              callbackUrl (POST /v1/platform/auth/idp/finish) and navigates
              the window there — landing on /auth/callback for the PKCE
              exchange, identical to the email path.
            */}
            <Route path="/auth/idp-callback" element={<IdpCallback />} />

            {/* Authenticated */}
            <Route
              path="/onboarding"
              element={
                <RequireAuth>
                  <Onboarding />
                </RequireAuth>
              }
            />

            {/*
              Sign-up approval gate (see RequireApproved). These three are
              authenticated but deliberately OUTSIDE RequireApproved + RequireOrg
              — the gated user is REDIRECTED here by RequireApproved, so they
              must be reachable without first clearing the gate (otherwise the
              redirect would loop). A new user fills /register, then waits at
              /pending-approval; a rejected user lands on /access-denied.
            */}
            <Route
              path="/register"
              element={
                <RequireAuth>
                  <Register />
                </RequireAuth>
              }
            />
            <Route
              path="/pending-approval"
              element={
                <RequireAuth>
                  <PendingApproval />
                </RequireAuth>
              }
            />
            <Route
              path="/access-denied"
              element={
                <RequireAuth>
                  <AccessDenied />
                </RequireAuth>
              }
            />

            {/*
              Accept an org invite. Authenticated but deliberately OUTSIDE
              the /app RequireOrg guard — a brand-new invitee has zero orgs
              and must be able to join before the no-org guard would bounce
              them to /onboarding.

              Bug #119: StashInviteToken runs BEFORE RequireAuth so the
              `?token=` is persisted to sessionStorage even for a signed-OUT
              invitee (whom RequireAuth immediately redirects to /login,
              unmounting AcceptInvite before it could stash anything itself).
              The token then survives the OIDC sign-in round-trip, and
              RequireOrg routes the post-login zero-org user back here to
              redeem it — instead of into the new-org onboarding flow.
            */}
            <Route
              path="/accept-invite"
              element={
                <StashInviteToken>
                  <RequireAuth>
                    <AcceptInvite />
                  </RequireAuth>
                </StashInviteToken>
              }
            />

            <Route
              path="/app"
              element={
                <RequireAuth>
                  <RedeemPendingInvite>
                    <RequireApproved>
                      <RequireOrg>
                        <AppLayout />
                      </RequireOrg>
                    </RequireApproved>
                  </RedeemPendingInvite>
                </RequireAuth>
              }
            >
              {/* /app: the page a sign-in or an org switch keeps, else the landing (Navigation review, D2, D11). */}
              <Route index element={<AppLanding />} />

              {/* Tier 1 — IoT */}
              {/* Devices (Navigation review, slice (c)): Gateways and Connect a device under one strip of tabs, at the
                  Gateways address. */}
              <Route element={<HubLayout hub={DEVICES} />}>
                <Route path="gateways" element={<Devices />} />
                <Route path="connect" element={<Connect />} />
              </Route>
              {/* Renamed devices → gateways (the unified noun); redirect keeps
                  old /app/devices links + bookmarks working. */}
              <Route path="devices" element={<Navigate to="/app/gateways" replace />} />
              {/* Floor capture (Navigation review, slice (c)): Data entry here, Rejections and the station view under
                  /app/twin, one strip of tabs; /app/capture opens a tab. */}
              <Route path="capture" element={<HubIndex hub={CAPTURE} />} />
              <Route element={<HubLayout hub={CAPTURE} />}>
                <Route path="data-entry" element={<DataEntry />} />
              </Route>
              {/* Registers folded into Data entry (Registers sub-tab). Redirect
                  keeps old /app/registers links working. */}
              <Route path="registers" element={<Navigate to="/app/data-entry" replace />} />
              <Route path="dashboards" element={<Dashboards />} />
              <Route path="dashboards/:id" element={<DashboardView />} />
              <Route path="alarms" element={<Alarms />} />
              <Route path="computed-tags" element={<ComputedTags />} />
              {/* Catalog (Navigation review, slice (c)): its own address opens its last used tab, each tab at its page's
                  own address under /app/twin. */}
              <Route path="catalog" element={<HubIndex hub={CATALOG} />} />

              {/* Vendor — cross-customer console (vendor-admin only; self-guards) */}
              <Route path="vendor" element={<VendorDashboard />} />
              <Route path="vendor/onboard" element={<VendorOnboard />} />
              <Route path="vendor/customers/:orgId" element={<VendorCustomerDetail />} />

              {/* Settings, one page with tabs (the 2026-10-06 next-phase plan, step 3): the org's look, people, plant
                  time, email, security, agent keys, packs and the tours. Admin, Plant time and Tutorials land on their
                  tabs. */}
              <Route path="settings" element={<SettingsPage />} />
              <Route path="admin" element={<Navigate to="/app/settings" replace />} />
              <Route path="plant-time" element={<Navigate to="/app/settings?tab=plant-time" replace />} />

              {/* Platform super-admin console (self-guards on isPlatformAdmin) */}
              <Route path="platform" element={<PlatformConsole />} />

              {/* SIM / upload agent studio — the agent's own workspace, distinct
                  from the operational portal (event-structure authoring + runner;
                  sources + upload + commissioning land here next). */}
              <Route path="sim" element={<SimStudio />} />


              {/* The tours' list is the Help and tutorials tab of Settings; one tour plays on its own page. */}
              <Route path="tutorials" element={<Navigate to="/app/settings?tab=help" replace />} />
              <Route
                path="tutorials/:id"
                element={
                  <Suspense fallback={<LazyFallback label="tutorial" />}>
                    <TutorialPlayerRoute />
                  </Suspense>
                }
              />

              {/* Dev-only widget catalog preview (not linked from nav) */}
              {/* Dev-only fixture catalog — Vite strips it (and its fixtures)
                  from the production bundle via the static import.meta.env.DEV. */}
              {import.meta.env.DEV && (
                <Route path="_widgets" element={<WidgetsPreview />} />
              )}

              {/* A station (a named group of the plant model's equipment) at its own address (station workspaces, N2);
                  the list is Plant model's Stations tab, so the bare address goes there. */}
              <Route path="stations" element={<Navigate to="/app/twin/stations" replace />} />
              <Route
                path="stations/:stationId"
                element={
                  <Suspense fallback={<LazyFallback label="station" />}>
                    <StationViewPage />
                  </Suspense>
                }
              />

              {/* Tier 2 — Twin (lazy + pack-gated) */}
              <Route
                path="twin/*"
                element={
                  <Suspense fallback={<LazyFallback />}>
                    <TwinRoutes />
                  </Suspense>
                }
              />
            </Route>

            {/* Catch-all */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </CopilotProvider>
          </GatewaysProvider>
          </AlarmsProvider>
          </InsightsProvider>
          </ImportsProvider>
          </ReportsProvider>
          </DashboardsProvider>
          </ComputedTagsProvider>
          </TracePathProvider>
          </PlantProvider>
          </ToastProvider>
        </OrgThemeProvider>
        </OrgProvider>
      </AuthProvider>
    </BrowserRouter>
      {import.meta.env.DEV && (
        <Suspense fallback={null}>
          <ReactQueryDevtools initialIsOpen={false} />
        </Suspense>
      )}
    </QueryClientProvider>
  );
}

function LazyFallback({ label = "twin" }: { label?: string }) {
  return (
    <div className="flex h-full items-center justify-center p-8">
      <div className="text-sm text-on-surface-muted">Loading {label}…</div>
    </div>
  );
}

/**
 * Persist the `?token=` from an /accept-invite URL to sessionStorage on mount,
 * BEFORE the wrapped RequireAuth can redirect a signed-out invitee to /login
 * (bug #119). Renders its children unchanged — it's a pure side-effect gate.
 * Stashing here (above RequireAuth) is what lets the token outlive the OIDC
 * sign-in round-trip that would otherwise drop the query string.
 */
function StashInviteToken({ children }: { children: ReactNode }) {
  const [params] = useSearchParams();
  const token = params.get("token");
  const stashedRef = useRef(false);
  if (token && !stashedRef.current) {
    stashedRef.current = true;
    stashInviteToken(token);
  }
  return <>{children}</>;
}

/**
 * No-org guard (multi-tenancy Phase F). An authenticated user with zero org
 * memberships is sent to /onboarding to create their first org. Gated on
 * identityLoaded so a member isn't bounced through /onboarding during the
 * sub-second /auth/me fetch (orgs is briefly [] before it resolves).
 *
 * Inert pre-cutover: AUTH_AUTOJOIN_DEFAULT_ORG=true means every login still
 * lands in the 'default' org, so orgs.length is never 0. It only starts
 * firing once the cutover flag flips (Phase G) and new signups arrive
 * org-less. No loop risk — /onboarding is a sibling route, not under /app.
 *
 * Bug #119: a freshly-signed-in invitee also has zero orgs at this point (they
 * haven't joined yet). If they arrived via an invite link, the token was
 * stashed in sessionStorage before the sign-in round-trip — so route them to
 * /accept-invite to redeem it and JOIN THE INVITING ORG, instead of dumping
 * them into the new-org creation flow. /accept-invite is outside this guard,
 * so there's no redirect loop; once joined, orgs is non-empty and this guard
 * passes through normally.
 */
/**
 * Sign-up approval gate. Sits between RequireAuth and RequireOrg on /app, so it
 * only ever sees an authenticated user. Routes a not-yet-approved user to the
 * right step of the gate:
 *
 *   - platform admins                          → pass (always bypass)
 *   - approvalStatus 'approved' OR undefined    → pass (undefined = legacy /
 *       grandfathered, or auth/me not yet resolved — don't gate on a missing
 *       value)
 *   - 'pending'  && !approvalSubmitted          → /register (fill the form)
 *   - 'pending'  &&  approvalSubmitted          → /pending-approval (wait)
 *   - 'rejected'                                → /access-denied
 *
 * Waits on identityLoaded first so a member isn't bounced mid-fetch (mirrors
 * RequireOrg). The three target routes are siblings of /app (outside this
 * guard), so the redirects can't loop. Inert in stub mode — STUB_USER is
 * 'approved'.
 */
/**
 * Redeem a stashed invite token as soon as auth resolves, ABOVE the approval /
 * org guards. The click-to-accept invite flow (StashInviteToken →
 * sessionStorage) previously only redeemed inside RequireOrg's `orgs.length === 0`
 * branch — which never holds in prod, because AUTH_AUTOJOIN_DEFAULT_ORG=true
 * lands every new user in the 'default' org first. Running the redemption here
 * (independent of org count / approval status) means a pending invite always
 * takes the user to /accept-invite to JOIN THE INVITING ORG.
 *
 * No loop: /accept-invite clears the token after accept, and it is a top-level
 * route OUTSIDE /app — so this guard (which only wraps /app) never intercepts
 * it. Gated on identityLoaded so we don't redirect mid-/auth/me fetch.
 */
function RedeemPendingInvite({ children }: { children: ReactNode }) {
  const { identityLoaded } = useAuth();
  const token = peekInviteToken();
  if (identityLoaded && token) {
    return <Navigate to={`/accept-invite?token=${encodeURIComponent(token)}`} replace />;
  }
  return <>{children}</>;
}

function RequireApproved({ children }: { children: ReactNode }) {
  const { identityLoaded, user } = useAuth();
  const { isPlatformAdmin } = useOrg();

  // Hold until the platform identity resolves — approvalStatus is undefined
  // until then, and we'd rather wait a beat than flash a redirect.
  if (!identityLoaded) {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-on-surface-muted">
        Loading session…
      </div>
    );
  }

  // Platform admins always bypass the gate.
  if (isPlatformAdmin) return <>{children}</>;

  const status = user?.approvalStatus;

  // Approved (or no status at all → legacy / grandfathered) passes through.
  if (status === undefined || status === "approved") return <>{children}</>;

  if (status === "rejected") return <Navigate to="/access-denied" replace />;

  // status === "pending"
  if (!user?.approvalSubmitted) return <Navigate to="/register" replace />;
  return <Navigate to="/pending-approval" replace />;
}

function RequireOrg({ children }: { children: ReactNode }) {
  const { identityLoaded } = useAuth();
  const { orgs } = useOrg();
  if (identityLoaded && orgs.length === 0) {
    const pendingInvite = peekInviteToken();
    if (pendingInvite) {
      return (
        <Navigate
          to={`/accept-invite?token=${encodeURIComponent(pendingInvite)}`}
          replace
        />
      );
    }
    return <Navigate to="/onboarding" replace />;
  }
  return <>{children}</>;
}

function AuthCallback() {
  // react-oidc-context's AuthProvider auto-processes the ?code=&state=
  // in the URL on mount and flips isAuthenticated to true once the
  // code-exchange completes. We observe that flip with useEffect and
  // navigate to /app via React Router (which onSigninCallback in
  // lib/auth.tsx CAN'T do — that handler runs outside the router and
  // window.history.replaceState alone doesn't trigger React Router
  // re-renders).
  const auth = useOidcAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (auth.isAuthenticated) {
      navigate("/app", { replace: true });
    }
  }, [auth.isAuthenticated, navigate]);
  return (
    <div className="flex h-screen items-center justify-center bg-surface text-sm text-on-surface-muted">
      {auth.error ? `Sign-in failed: ${auth.error.message}` : "Completing sign-in…"}
    </div>
  );
}

function IdpCallback() {
  // Zitadel appends ?id=<intentId>&token=<intentToken>[&user=] to the
  // success URL we registered (which already carries ?authRequest=). We
  // forward {authRequest, id, token} to the backend, which retrieves the
  // intent, auto-provisions the user on first sign-in, creates a session,
  // and finalizes the AuthRequest into the OIDC callbackUrl. A window
  // navigation (not React Router) is required — callbackUrl is on
  // Zitadel's origin and carries ?code=&state= for the PKCE exchange.
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;

    const authRequest = params.get("authRequest");
    const intentId = params.get("id");
    const intentToken = params.get("token");

    if (!authRequest || !intentId || !intentToken) {
      navigate("/login?idp_error=1", { replace: true });
      return;
    }

    void (async () => {
      try {
        const resp = await api.post<{ callbackUrl: string }>(
          "/v1/platform/auth/idp/finish",
          { authRequestId: authRequest, intentId, intentToken },
        );
        window.location.assign(resp.callbackUrl);
      } catch {
        navigate(
          `/login?authRequest=${encodeURIComponent(authRequest)}&idp_error=1`,
          { replace: true },
        );
      }
    })();
  }, [params, navigate]);

  return (
    <div className="flex h-screen items-center justify-center bg-surface text-sm text-on-surface-muted">
      Completing sign-in…
    </div>
  );
}

function NotFound() {
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-3 bg-surface text-on-surface">
      <h1 className="text-2xl font-semibold">404 — Not found</h1>
      <a className="text-sm text-brand underline" href="/">
        Back to home
      </a>
    </div>
  );
}
